import React, { ReactElement } from 'react';
import { Container } from '@/components/Containers/Container';
import { Typography } from '@/components/Typography/Typography';
import { BlackButton } from '@/components/ButtonsCollection/BlackButton/BlackButton';
import { PATH_AGENT, PATH_CUSTOMER } from '@/constants/spa-routes';
import { COLORS } from '@/constants/colors';
import { SmallArrowIcon } from '@/assets/icons/smallArrowIcon';
import Image from 'next/image';
import logoCustomer from '@/public/img/customer-logo-shadow.webp';
import logoAgent from '@/public/img/agent-logo-shadow.webp';
import { TabsPart } from '@/components/PagesSections/FreshdeskIntegration/AiForFreshdesk/TabsPart';

const tabsContent1: {
  mainTitle: ReactElement;
  tabsContent: {
    id: number;
    title: string;
    description: string;
    img: string;
    mobileImg: string;
  }[];
} = {
  mainTitle: (
    <div className="lg:w-2/5 flex flex-col lg:gap-7.5-vw gap-6">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center lg:gap-2-vw gap-2 pb-1">
          <Image
            className="rounded overflow-hidden h-full lg:max-h-[1.736vw] max-h-[25px] lg:max-w-[1.736vw] max-w-[25px]"
            src={logoCustomer}
            alt="logoCustomer"
          />
          <Typography $type="PRegular" className="w-full">
            <b>co</b>support <b>customer</b>
          </Typography>
        </div>
        <Typography $type="Heading2Big" $weight="700" className="font-formula">
          Generative AI Chatbot
        </Typography>
        <Typography
          $type="Heading4"
          $weight="400"
          className="font-jamjuree opacity-70 lg:max-w-[70%]"
        >
          That handles support requests independently
        </Typography>
      </div>
      <div className="w-fit block max-lg:w-full">
        <BlackButton
          className="max-lg:w-full"
          linkBtnClassName="max-lg:w-full"
          url={PATH_CUSTOMER.ROOT}
        >
          <div className="button-text flex justify-center items-center lg:gap-4-vw gap-4">
            <Typography $color={COLORS.WHITE} as="span" $type="PRegular" $weight="700">
              Learn More
            </Typography>
            <SmallArrowIcon color="purple" />
          </div>
        </BlackButton>
      </div>
    </div>
  ),
  tabsContent: [
    {
      id: 0,
      title: 'Autonomous AI Chatbot',
      description: 'The AI assistant provides finalized responses without human involvement',
      img: '/img/freshdesk-integration/trained-section/tabs2-image-1.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs2-image-1-mobile.webp',
    },
    {
      id: 1,
      title: 'High Level of Accuracy and Personalization',
      description:
        'Each response is tailored to the query, considering case details, with a lower risk of hallucinations than GPT.',
      img: '/img/freshdesk-integration/trained-section/tabs2-image-2.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs2-image-2-mobile.webp',
    },
    {
      id: 2,
      title: 'Flexible Query Routing',
      description:
        'Certain queries are directed to human agents for a comprehensive customer experience.',
      img: '/img/freshdesk-integration/trained-section/tabs2-image-3.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs2-image-3-mobile.webp',
    },
  ],
};

const tabsContent2: {
  mainTitle: ReactElement;
  tabsContent: {
    id: number;
    title: string;
    description: string;
    img: string;
    mobileImg: string;
  }[];
} = {
  mainTitle: (
    <div className="lg:w-2/5 flex flex-col lg:gap-7.5-vw gap-6">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center lg:gap-2-vw gap-2 pb-1">
          <Image
            className="rounded overflow-hidden h-full lg:max-h-[1.736vw] max-h-[25px] lg:max-w-[1.736vw] max-w-[25px]"
            src={logoAgent}
            alt="logoAgent"
          />
          <Typography $type="PRegular" className="w-full">
            <b>co</b>support <b>agent</b>
          </Typography>
        </div>
        <Typography $type="Heading2Big" $weight="700" className="font-formula">
          Human Agent Assistant
        </Typography>
        <Typography
          $type="Heading4"
          $weight="400"
          className="font-jamjuree opacity-70 lg:max-w-[70%]"
        >
          That generates ready-to-go reply suggestions for support agents
        </Typography>
      </div>
      <div className="w-fit block max-lg:w-full">
        <BlackButton
          className="max-lg:w-full"
          linkBtnClassName="max-lg:w-full"
          url={PATH_AGENT.ROOT}
        >
          <div className="button-text flex justify-center items-center lg:gap-4-vw gap-4">
            <Typography $color={COLORS.WHITE} as="span" $type="PRegular" $weight="700">
              Learn More
            </Typography>
            <SmallArrowIcon color="blue" />
          </div>
        </BlackButton>
      </div>
    </div>
  ),
  tabsContent: [
    {
      id: 0,
      title: 'AI-Powered Draft Selection for Agents',
      description: 'The AI assistant offers two draft responses for human agents to choose from.',
      img: '/img/freshdesk-integration/trained-section/tabs1-image-1.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs1-image-1-mobile.webp',
    },
    {
      id: 1,
      title: 'Customizable Drafts for Personalized Responses',
      description:
        'Agents can then edit the selected draft and fill in placeholders with the relevant details.',
      img: '/img/freshdesk-integration/trained-section/tabs1-image-2.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs1-image-2-mobile.webp',
    },
    {
      id: 2,
      title: 'Human-Centric Responses Enhanced by AI',
      description:
        "The response is the result of a human agent's decision, based on an AI assistant's suggestions.",
      img: '/img/freshdesk-integration/trained-section/tabs1-image-3.webp',
      mobileImg: '/img/freshdesk-integration/trained-section/tabs1-image-3-mobile.webp',
    },
  ],
};

export const AiForFreshdesk = () => {
  return (
    <section className="relative bg-light">
      <Container>
        <div className="z-10 md:pt-17-vw pt-15 md:pb-17-vw pb-6">
          <div className="flex flex-col justify-center items-center md:gap-12.5-vw gap-6">
            <div className="w-full flex-col flex justify-center items-center lg:gap-2.5-vw gap-2.5 md:max-w-[65.444vw]">
              <Typography
                $type="Heading1"
                as="h2"
                className="md:text-center font-formula"
                $weight="700"
              >
                AI Copilot for Freshdesk trained on your specific data
              </Typography>
              <Typography $type="Heading4" as="p" className="md:text-center" $weight="400">
                AI-powered assistant for Freshdesk, designed for customer support teams. It
                functions as a web browser extension, trained on{' '}
                <b>
                  <i>historical ticket data</i>
                </b>{' '}
                and existing customer support documentation, and focuses on domain-specific
                information to provide consistent and relevant responses.
              </Typography>
            </div>
            <TabsPart tab={tabsContent1} className="bg-purple_ley lg:mt-5-vw mt-4" />
            <TabsPart tab={tabsContent2} className="bg-malibu_sky" />
          </div>
        </div>
      </Container>
    </section>
  );
};
